// @ts-nocheck
import { useState, lazy, Suspense } from "react";
import { MarkdownMentionText } from "./markdown-mention-text";
import { ActionConfirmationBubble } from "./action-confirmation-bubble";
import { isAIAgentUser } from "@shared/schema";
import { Bot, MoreHorizontal, Pencil, Trash2, Check, X, Copy, FileText, Smile, AlertTriangle, Brain, RefreshCw } from "lucide-react";
import type { ChatAttachment } from "@shared/schema/chat";
import { formatMessageTimestamp } from "@/lib/chat-utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const EmojiPickerInner = lazy(async () => {
  const [{ default: Picker }, { default: data }] = await Promise.all([
    import('@emoji-mart/react'),
    import('@emoji-mart/data'),
  ]);
  return {
    default: ({ onSelect }: { onSelect: (native: string) => void }) => (
      <Picker
        data={data}
        onEmojiSelect={(e: { native: string }) => onSelect(e.native)}
        theme="auto"
        previewPosition="none"
        skinTonePosition="none"
      />
    ),
  };
});

interface MessageBubbleProps {
  message: {
    id: string;
    senderId: string;
    text?: string | null;
    mentions?: Array<{ userId?: string; claimId?: string; offset: number; length: number }> | null;
    attachments?: ChatAttachment[] | null;
    editedAt?: string | Date | null;
    createdAt: string | Date;
    sender?: {
      firstName?: string;
      lastName?: string;
      email?: string;
    } | null;
  };
  isCurrentUser: boolean;
  currentUserId?: string;
  agents?: Array<{ id: string; displayName: string }>;
  participants?: Array<{ userId: string; user?: { id: string; firstName?: string; lastName?: string; email?: string; role?: string } }>;
  onEdit?: (messageId: string, newText: string) => void;
  onDelete?: (messageId: string) => void;
  onApprove?: (pendingToolCallId: string) => void;
  onDecline?: (pendingToolCallId: string) => void;
  isActionLoading?: boolean;
  subtitleOverride?: React.ReactNode;
  reactions?: Array<{ emoji: string; count: number; userIds: string[] }>;
  onReact?: (messageId: string, emoji: string) => void;
  onClearMemory?: () => void;
  isClearMemoryPending?: boolean;
  onRegenerate?: () => void;
}

export function MessageBubble({ message, isCurrentUser, currentUserId, agents, participants, onEdit, onDelete, onApprove, onDecline, isActionLoading, subtitleOverride, reactions, onReact, onClearMemory, isClearMemoryPending, onRegenerate }: MessageBubbleProps) {
  const mentionUsers = participants?.filter(p => p.user).map(p => ({
    id: p.userId,
    firstName: p.user?.firstName,
    lastName: p.user?.lastName,
    email: p.user?.email,
    role: p.user?.role,
  }));
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(message.text || '');
  const [copied, setCopied] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const isSystem = message.senderId === 'system';
  const matchedAgent = agents?.find(a => a.id === message.senderId);
  const isBot = isAIAgentUser(message.senderId) || !!matchedAgent;
  const isMentioned = currentUserId && message.mentions?.some(m => m.userId && m.userId === currentUserId);

  if (isSystem) {
    return (
      <div className="flex justify-center" data-testid={`message-${message.id}`}>
        <div className="text-xs text-muted-foreground italic py-1" data-testid={`message-content-${message.id}`}>
          {message.text}
        </div>
      </div>
    );
  }

  if (message.messageType === 'action_confirmation' && message.metadata) {
    return (
      <ActionConfirmationBubble
        message={message}
        agentName={matchedAgent?.displayName}
        onApprove={(id) => onApprove?.(id)}
        onDecline={(id) => onDecline?.(id)}
        isLoading={isActionLoading}
      />
    );
  }

  if (message.messageType === 'system_error') {
    const senderName = matchedAgent
      ? matchedAgent.displayName
      : message.sender
        ? `${message.sender.firstName || ''} ${message.sender.lastName || ''}`.trim() || message.sender.email || 'Unknown User'
        : 'Unknown User';
    const showClearMemory = message.metadata?.errorKind === 'invalid_request' && !!onClearMemory;
    return (
      <div className="flex justify-start" data-testid={`message-${message.id}`}>
        <div
          className="max-w-[70%] rounded-lg p-3 bg-destructive/10 border border-destructive/30"
          data-testid={`message-bubble-${message.id}`}
        >
          <div className="text-xs font-semibold mb-1 flex items-center gap-1 text-destructive">
            {senderName}
            {isBot && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wider text-destructive bg-destructive/10 px-1 py-0.5 rounded">
                <Bot className="h-2.5 w-2.5" />
                Bot
              </span>
            )}
          </div>
          <div className="flex items-start gap-2 text-destructive text-sm" data-testid={`message-content-${message.id}`}>
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{message.text}</span>
          </div>
          {showClearMemory && (
            <div className="mt-2">
              <Button
                size="sm"
                variant="outline"
                className="h-7 border-destructive/40 text-destructive hover:bg-destructive/10 text-xs"
                onClick={onClearMemory}
                disabled={!!isClearMemoryPending}
                data-testid={`clear-memory-${message.id}`}
              >
                <Brain className="h-3 w-3 mr-1" />
                Clear Memory
              </Button>
            </div>
          )}
          <div className="text-xs opacity-70 mt-1 text-destructive" data-testid={`message-time-${message.id}`}>
            {formatMessageTimestamp(message.createdAt)}
          </div>
        </div>
      </div>
    );
  }

  const senderName = matchedAgent
    ? matchedAgent.displayName
    : message.sender
      ? `${message.sender.firstName || ''} ${message.sender.lastName || ''}`.trim() || message.sender.email || 'Unknown User'
      : 'Unknown User';

  const handleSaveEdit = () => {
    if (editText.trim() && editText.trim() !== message.text) {
      onEdit?.(message.id, editText.trim());
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditText(message.text || '');
    setIsEditing(false);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(message.text || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      data-testid={`message-${message.id}`}
      className={`flex ${isCurrentUser ? "justify-end" : "justify-start"} group`}
    >
      <div
        className={`max-w-[70%] min-w-0 break-words rounded-lg p-3 relative ${
          isCurrentUser
            ? "bg-primary text-primary-foreground"
            : isMentioned
              ? "bg-primary/10 border border-primary/30"
              : "bg-muted"
        }`}
        data-testid={`message-bubble-${message.id}`}
      >
        {!isCurrentUser && (
          <div className="text-xs font-semibold mb-1 flex items-center gap-1" data-testid={`message-sender-${message.id}`}>
            {senderName}
            {isBot && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary bg-primary/10 px-1 py-0.5 rounded">
                <Bot className="h-2.5 w-2.5" />
                Bot
              </span>
            )}
          </div>
        )}

        {isEditing ? (
          <div className="flex items-center gap-1">
            <Input
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveEdit();
                if (e.key === 'Escape') handleCancelEdit();
              }}
              className="h-7 text-sm text-foreground bg-background"
              autoFocus
              data-testid={`edit-message-input-${message.id}`}
            />
            <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={handleSaveEdit} data-testid={`edit-message-save-${message.id}`}>
              <Check className="h-3.5 w-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={handleCancelEdit} data-testid={`edit-message-cancel-${message.id}`}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <div className="text-sm break-words" data-testid={`message-content-${message.id}`}>
            <MarkdownMentionText text={message.text || ''} mentions={message.mentions} isOutgoing={isCurrentUser} users={mentionUsers} />
          </div>
        )}

        {message.attachments && message.attachments.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {message.attachments.map((att, i) => {
              const isImage = att.mimeType.startsWith('image/');
              if (isImage) {
                return (
                  <a key={i} href={att.fileUrl} target="_blank" rel="noopener noreferrer" className="block">
                    <img
                      src={att.fileUrl}
                      alt={att.fileName}
                      className="max-w-full max-h-60 rounded-md object-contain"
                      loading="lazy"
                    />
                  </a>
                );
              }
              return (
                <a
                  key={i}
                  href={att.fileUrl}
                  download={att.fileName}
                  className={`flex items-center gap-2 p-2 rounded-md text-xs ${
                    isCurrentUser ? 'bg-primary-foreground/10 hover:bg-primary-foreground/20' : 'bg-background hover:bg-accent'
                  } transition-colors`}
                >
                  <FileText className="h-4 w-4 shrink-0" />
                  <span className="truncate">{att.fileName}</span>
                  <span className="text-[10px] opacity-70 shrink-0">
                    {att.size < 1024 * 1024
                      ? `${Math.round(att.size / 1024)} KB`
                      : `${(att.size / 1024 / 1024).toFixed(1)} MB`}
                  </span>
                </a>
              );
            })}
          </div>
        )}

        {reactions && reactions.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {reactions.map(({ emoji, count, userIds }) => {
              const reacted = currentUserId ? userIds.includes(currentUserId) : false;
              return (
                <button
                  key={emoji}
                  onClick={() => onReact?.(message.id, emoji)}
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs border transition-colors ${
                    reacted
                      ? 'bg-primary/10 border-primary/30 text-primary'
                      : 'bg-background border-border text-muted-foreground hover:bg-muted'
                  }`}
                  data-testid={`reaction-${message.id}-${emoji}`}
                >
                  <span>{emoji}</span>
                  <span className="font-medium">{count}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="text-xs opacity-70 mt-1 flex items-center gap-1" data-testid={`message-time-${message.id}`}>
          {subtitleOverride ?? (
            <>
              {formatMessageTimestamp(message.createdAt)}
              {message.editedAt && <span className="text-[10px] italic">(edited)</span>}
            </>
          )}
        </div>

        {!isEditing && (
          <div className="absolute -top-2 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
            <button
              onClick={handleCopy}
              className="h-6 w-6 rounded-full bg-background border shadow-sm flex items-center justify-center hover:bg-muted"
              data-testid={`message-copy-${message.id}`}
            >
              {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5 text-muted-foreground" />}
            </button>
            {onReact && (
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger asChild>
                  <button
                    className="h-6 w-6 rounded-full bg-background border shadow-sm flex items-center justify-center hover:bg-muted"
                    data-testid={`message-react-${message.id}`}
                  >
                    <Smile className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </PopoverTrigger>
                <PopoverContent side="top" align="start" className="w-auto p-0 border-none shadow-xl">
                  <Suspense fallback={<div className="w-[352px] h-[435px] flex items-center justify-center text-xs text-muted-foreground">Loading...</div>}>
                    <EmojiPickerInner
                      onSelect={(native) => {
                        onReact(message.id, native);
                        setPickerOpen(false);
                      }}
                    />
                  </Suspense>
                </PopoverContent>
              </Popover>
            )}
            {isBot && onRegenerate && (
              <button
                onClick={onRegenerate}
                className="h-6 w-6 rounded-full bg-background border shadow-sm flex items-center justify-center hover:bg-muted"
                title="Regenerate response"
                data-testid={`button-regenerate-${message.id}`}
              >
                <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            )}
            {isCurrentUser && !isBot && (onEdit || onDelete) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="h-6 w-6 rounded-full bg-background border shadow-sm flex items-center justify-center hover:bg-muted" data-testid={`message-actions-${message.id}`}>
                    <MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-32">
                  {onEdit && (
                    <DropdownMenuItem onClick={() => { setEditText(message.text || ''); setIsEditing(true); }} data-testid={`message-edit-${message.id}`}>
                      <Pencil className="h-3.5 w-3.5 mr-2" />
                      Edit
                    </DropdownMenuItem>
                  )}
                  {onDelete && (
                    <DropdownMenuItem onClick={() => onDelete(message.id)} className="text-destructive" data-testid={`message-delete-${message.id}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
